import { effectSlot } from '@agavi/blocks';
import { ArrowDown, ChevronDown, ChevronRight, Eye, KeyRound, Scale, Trash2, Wrench } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlowConditionList, FlowConditionSummary } from './flow-condition-list';
import { FlowMatchMode } from './flow-match-mode';
import { FlowPausedNotice, PAUSE_MESSAGE } from './flow-paused-notice';
import { FlowPickerMenu } from './flow-picker-menu';
import { ARMED_ROW, FlowRemoveButton } from './flow-remove-button';

import type { Block, Task } from '@/types/experience';
import type { FlowChange, FlowEffect } from '@agavi/blocks';

import { AddRowButton } from '@/components/ui/add-row-button';
import { EditableTitle } from '@/components/ui/editable-heading';
import { IconButton } from '@/components/ui/icon-button';
import { describeEffect, describeEffectParts, effectGroups, effectTone, parseEffectKey, usedEffectSlots, type EffectTone } from '@/lib/flow-effects';
import { flowCardKey, getCardProblem } from '@/lib/flow-utils';

/**
 * One rule in the Changes list: conditions in, effects out, never a destination.
 *
 * Changes and Navigation are two lists resolved in separate passes, not one ordered list. In one
 * list an unbounded effect ("after 3 attempts, unlock manual entry") sat above every route and
 * silently shadowed it, and the fixes for that were all worse than the split: an effect that
 * short-circuits nothing needs no order at all, so it has none.
 *
 * The title IS editable, unlike a feedback card's. That rule — a derived name beats one that
 * drifts — holds where the name must keep naming the answer that triggers it. Here the derived
 * name describes only the EFFECT, so two changes unlocking the same bypass under different
 * conditions were given the same name and could not be told apart. The derived sentence stays as
 * what shows when the teacher has written nothing.
 */

interface FlowChangeCardProps {
  change: FlowChange;
  blocks: Block[];
  /** For a `visits` condition's task picker — see `FlowConditionList`. */
  tasks: Task[];
  currentTaskId: string;
  /** Whether a student can arrive here twice — see `reenterableTasks`. */
  reenterable: boolean;
  /**
   * Every change in the task, so the menu can refuse a setting another card already opens.
   *
   * Exclusivity is task-wide rather than per card: a setting is opened once and then it is open,
   * so a second change opening it again is a no-op the teacher would have to reason about.
   */
  allChanges: FlowChange[];
  /**
   * Controlled by the panel, which keeps ONE change open at a time.
   *
   * The card used to own this. Several open at once made a column of tall, near-identical cards
   * with nothing to tell them apart while scrolling, so the panel holds the open id and closing
   * the others is automatic rather than housekeeping the teacher has to remember.
   */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Flagged, not paused: a duplicate still fires, so it is told about rather than stopped. */
  duplicateOf?: string;
  onChange: (change: FlowChange) => void;
  onDelete: () => void;
}

/** What this change does, as a sentence — the card's name. */
export function changeTitle(change: FlowChange, blocks: Block[], t: (key: string, opts?: Record<string, unknown>) => string): string {
  /*
   * A CHANGE FROM THE DATABASE MAY NOT BE A CHANGE.
   *
   * The list is stored loosely, and for a while the API destroyed it on the way in -- class
   * transformer coerced each Change object to its reflected type and wrote `[]` in its place.
   * Two tasks in one real experience still hold `changes: [[]]`, and reading `.effects.length`
   * off one of those threw and took the whole Inspect panel down with it: a task opened in
   * Preview rendered an error boundary instead of a panel.
   *
   * Named rather than nullish-chained so the reason survives: this is bad DATA, not an
   * optional field. `resolveChanges` drops the same corpses on the engine side.
   */
  const effects = Array.isArray(change?.effects) ? change.effects : [];
  if (effects.length === 0) return t('task.flow.changes.untitled');
  return effects
    .map((e) => describeEffect(e, blocks, t))
    .join(t('task.flow.changes.effectJoin'));
}

export function FlowChangeCard({ change, blocks, tasks, currentTaskId, reenterable, allChanges, collapsed, onToggleCollapsed, duplicateOf, onChange, onDelete }: FlowChangeCardProps) {
  const { t } = useTranslation();
  const [deleteArmed, setDeleteArmed] = useState(false);
  /**
   * Whether this card has been LEFT, which is what makes incompleteness a finding.
   *
   * A card is incomplete the moment it is created -- that is the definition of new, not news.
   * Leaving it incomplete is the news. Collapsed counts, and so does focus moving out of it
   * while it is still open, which is the other way to walk away from one.
   */
  const [blurred, setBlurred] = useState(false);
  const settled = collapsed || blurred;
  const problem = getCardProblem(change, blocks, settled, { effects: change.effects });
  const derived = changeTitle(change, blocks, t);

  const effectListRef = useRef<HTMLDivElement>(null);
  // Slots, not keys: two visibility modes on one block are a contradiction rather than a
  // duplicate, and `effectSlot` is what knows the difference.
  const used = usedEffectSlots(allChanges, change.id);
  const groups = effectGroups(blocks, used, t);
  /**
   * Spent rows stay, greyed, exactly as the feedback picker leaves a condition already on an
   * event. Hiding them was tried and withdrawn: the menu is how a teacher checks what a block
   * CAN do, and a block that silently disappears once its one row is taken cannot be told apart
   * from a block that never had anything to offer.
   */
  const allUsed = groups.every((g) => g.options.every((o) => o.disabled));

  function addEffect(key: string) {
    const effect = parseEffectKey(key);
    if (!effect) return;
    const slot = effectSlot(effect);
    // Guarded again here, not only in the menu: the picker greys these rows and skips them on the
    // keyboard, but the key arrives as a string and the list it was built from can have moved on.
    if (used.own.has(slot)) return;
    onChange({ ...change, effects: [...change.effects, effect] });
  }

  function removeEffect(index: number) {
    onChange({ ...change, effects: change.effects.filter((_, i) => i !== index) });
  }

  return (
    // `overflow-hidden` so the header band's corners follow the card's, and no padding on the
    // shell: the band has to reach the edges to read as a band rather than an inset strip.
    <div
      /* See `flowCardKey` — how the board's problems panel finds this card to open and ring it. */
      data-flow-card={flowCardKey('change', change.id)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setBlurred(true); }}
      className={`flex shrink-0 flex-col overflow-hidden rounded-xl border transition-colors ${
        deleteArmed ? 'border-ws-warning-fg bg-ws-warning-bg/40'
          : problem || duplicateOf ? 'border-ws-warning-fg bg-ws-warning-fg/[.04]'
          : 'border-ws-border'
      }`}
    >
      {/* A shaded title band, the same treatment the condition menu gives its group headers.
          Several of these cards stacked were a wall of identical rounded rectangles with nothing
          marking where one ended and the next began; a filled header draws that line. */}
      <div className={`flex items-start gap-2 border-b px-3 py-2 ${
        deleteArmed ? 'border-ws-warning-fg' : 'border-ws-border bg-ws-btn'
      }`}>
        <span
          aria-hidden='true'
          className='flex h-9 w-9 shrink-0 items-center justify-center self-center rounded-md border border-ws-border-strong text-ws-text'
        >
          <Wrench className='h-4 w-4' />
        </span>
        {/* A NAME, given on creation ("Change Event 2") and editable after. The derived sentence
            is the fallback for a change saved before titles existed, and becomes the collapsed
            summary below — a name alone does not say what the change does. */}
        <div className='min-w-0 flex-1 self-center'>
          {collapsed
            ? <p className='min-w-0 truncate text-sm font-semibold text-ws-text'>{change.title?.trim() || derived}</p>
            : (
              <EditableTitle
                size='sm'
                value={change.title?.trim() || derived}
                onCommit={(title) => onChange({ ...change, title })}
                ariaLabel={t('task.flow.changes.titleLabel')}
              />
            )}
        </div>
        <IconButton
          type='button'
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('task.flow.card.expandLabel') : t('task.flow.card.collapseLabel')}
          className='-mx-1 self-center'
        >
          {collapsed ? <ChevronRight className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
        </IconButton>
      </div>

      <div className='flex flex-col gap-3 p-3'>
      {!collapsed && (
        <>
      <FlowMatchMode value={change.matchMode} onChange={(matchMode) => onChange({ ...change, matchMode })} />

      <FlowConditionList
        conditions={change.conditions}
        blocks={blocks}
        tasks={tasks}
        currentTaskId={currentTaskId}
        reenterable={reenterable}
        matchMode={change.matchMode}
        onChange={(conditions) => onChange({ ...change, conditions })}
        problemKeys={problem?.conditionKeys}
        problemSeverity={problem ? 'warning' : undefined}
      />

      <div className='flex flex-col gap-1.5 border-t border-ws-border pt-3'>
        <span className='text-[10px] font-semibold uppercase tracking-wide text-ws-muted'>
          {t('task.flow.changes.thenLabel')}
        </span>

        <div ref={effectListRef} className='flex flex-col gap-1.5'>
          {change.effects.map((effect, index) => (
            <EffectRow
              key={effectSlot(effect)}
              effect={effect}
              blocks={blocks}
              onRemove={() => removeEffect(index)}
            />
          ))}
        </div>

        {groups.length === 0 ? (
          // Not a warning: a task whose blocks have nothing a change can alter simply has nothing
          // to offer here, which is the ordinary state of a task built only from display blocks.
          <p className='text-xs italic text-ws-muted'>{t('task.flow.changes.noCandidates')}</p>
        ) : (
          // The same grouped menu as Add Feedback, for the same reasons. It began as one button
          // per candidate block, which was readable while the only effect was a bypass on one of
          // two block types; across six block types and fourteen rows a button row is a wall.
          // Greying what the change already holds rather than dropping it also follows that menu:
          // the row it refers to is on screen right behind it, so vanishing reads as a bug.
          <FlowPickerMenu
            alignTo={effectListRef}
            sections={groups.map((g) => ({
              header: g.blockLabel,
              options: g.options.map((o) => ({ ...o, icon: <EffectGlyph effect={o.effect} /> })),
            }))}
            onChoose={addEffect}
            ariaLabel={t('task.flow.changes.addEffectLabel')}
            filterPlaceholder={t('task.flow.changes.filterEffects')}
            noMatchesLabel={t('task.flow.card.noMatches')}
            renderTrigger={(p) => (
              <AddRowButton
                {...p}
                inline
                icon={<Wrench aria-hidden='true' />}
                label={t('task.flow.changes.addEffect')}
                disabled={allUsed}
              />
            )}
          />
        )}
      </div>
        </>
      )}

      {/*
        Collapsed, the card reads as the whole rule: what triggers it, then what it does.
        It used to show only the effects, which left the title ("Change Event 2") and a sentence
        about a setting, and nothing at all about WHEN it fires -- so two changes doing the same
        thing under different conditions were indistinguishable until both were opened.
        Same parts as a collapsed event: conditions via the very component the events use, the
        block bolded, the glyph carrying the kind.

        Stacked with a CENTRED arrow between, not run together on one line. Either half can be a
        list -- three conditions above two effects -- and inline the eye loses which side of the
        connector it is on exactly when there is most to read. Down the middle, the join holds
        however long the two lists get.
      */}
      {collapsed && (
        <div className='flex flex-col items-center gap-1 text-xs text-ws-muted'>
          {change.conditions.length > 0 && (
            <div className='flex flex-wrap justify-center gap-x-2 gap-y-1'>
              <FlowConditionSummary conditions={change.conditions} blocks={blocks} tasks={tasks} matchMode={change.matchMode} />
            </div>
          )}
          {change.conditions.length > 0 && change.effects.length > 0 && (
            <ArrowDown aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-muted' />
          )}
          <div className='flex flex-wrap justify-center gap-x-2 gap-y-1'>
            {change.effects.length === 0
              ? <span className='italic'>{t('task.flow.changes.untitled')}</span>
              : change.effects.map((effect) => <EffectSummaryToken key={effectSlot(effect)} effect={effect} blocks={blocks} />)}
          </div>
        </div>
      )}

      {/* The foot: why it will not run on the left, the bin on the right -- the same strip an
          event card reserves for exactly this, so the two card types say it in one place. Shown
          while collapsed as well, because a stopped change should not need expanding to explain
          itself; only the bin waits for the card to be open. */}
      {(problem || duplicateOf || !collapsed) && (
        <div className='flex items-center justify-end gap-2'>
          <div className='flex min-w-0 flex-1 items-center'>
            <FlowPausedNotice
              pausedMessage={problem ? t(PAUSE_MESSAGE[problem.kind]) : undefined}
              duplicateMessage={duplicateOf ? t('task.flow.rule.duplicateOf', { title: duplicateOf }) : undefined}
            />
          </div>
          {!collapsed && (
          <>
          <div aria-live='assertive' aria-atomic='true' className='sr-only'>
            {deleteArmed ? t('blocks.actions.deleteArmed') : ''}
          </div>
          <IconButton
            type='button'
            variant='destructive'
            isArmed={deleteArmed}
            onClick={() => {
              if (!deleteArmed) { setDeleteArmed(true); return; }
              onDelete();
            }}
            onPointerLeave={(e) => { if (e.pointerType !== 'touch' && deleteArmed) setDeleteArmed(false); }}
            onBlur={() => { if (deleteArmed) setDeleteArmed(false); }}
            onKeyDown={(e) => { if (e.key === 'Escape' && deleteArmed) { e.stopPropagation(); setDeleteArmed(false); } }}
            aria-label={deleteArmed
              ? t('task.flow.changes.deleteConfirmLabel', { title: change.title?.trim() || derived })
              : t('task.flow.changes.deleteLabel', { title: change.title?.trim() || derived })}
          >
            <Trash2 className='h-4 w-4' />
          </IconButton>
          </>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

/**
 * The mark an effect wears, by what it OFFERS rather than by its `kind`.
 *
 * Same job the feedback glyphs do, and for the same reason: in a menu grouped under block
 * headers, unmarked rows read as more block names. Colour carries the family at a glance and the
 * glyph names it -- scales for a rule judged less strictly, an eye for showing the student more,
 * a key for the route out of a dead sensor.
 *
 * `ws-text-link` rather than `ws-primary` for the blue, which is the house rule for a chip or a
 * highlight: primary does not hold up against the dark surface.
 */
const TONE_GLYPH: Record<EffectTone, typeof KeyRound> = {
  relax: Scale,
  reveal: Eye,
  rescue: KeyRound,
};

const TONE_COLOR: Record<EffectTone, string> = {
  relax: 'text-ws-success-fg',
  reveal: 'text-ws-text-link',
  rescue: 'text-ws-warning-fg',
};

function EffectGlyph({ effect, className = 'h-3.5 w-3.5' }: { effect: FlowEffect; className?: string }) {
  const tone = effectTone(effect);
  const Glyph = TONE_GLYPH[tone];
  return <Glyph aria-hidden='true' className={`shrink-0 ${TONE_COLOR[tone]} ${className}`} />;
}

/** One effect in a collapsed card, set like a condition: block bolded, then the glyph, then what
 *  it does — so the two halves of the sentence are told apart at a glance. */
function EffectSummaryToken({ effect, blocks }: { effect: FlowEffect; blocks: Block[] }) {
  const { t } = useTranslation();
  const { block, action } = describeEffectParts(effect, blocks, t);
  return (
    <span className='inline-flex min-w-0 items-center gap-1 text-ws-text'>
      <span className='font-medium'>{block}</span>
      <EffectGlyph effect={effect} />
      <span className='min-w-0 truncate'>{action}</span>
    </span>
  );
}

function EffectRow({ effect, blocks, onRemove }: { effect: FlowEffect; blocks: Block[]; onRemove: () => void }) {
  const { t } = useTranslation();
  const [armed, setArmed] = useState(false);
  const known = blocks.some((b) => b.id === effect.blockId);
  const label = describeEffect(effect, blocks, t);
  return (
    // Armed outranks the unknown-block dashes, the same way it outranks a condition row's problem
    // tint: "about to be deleted" is the more urgent fact and answers the question the second
    // click is asking.
    <div className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs transition-colors ${
      armed ? ARMED_ROW : known ? 'border-ws-border' : 'border-dashed border-ws-border italic text-ws-muted'
    }`}>
      <EffectGlyph effect={effect} />
      <span className='min-w-0 flex-1 truncate text-ws-text'>{label}</span>
      <FlowRemoveButton
        label={t('task.flow.changes.removeEffect', { effect: label })}
        confirmLabel={t('common.confirmDelete')}
        armed={armed}
        onArmedChange={setArmed}
        onClick={onRemove}
      />
    </div>
  );
}
